import React from "react";
import {
  FileText,
  Scale,
  Shield,
  AlertTriangle,
  Users,
  Mail,
  Bot,
  Github,
  Search,
} from "lucide-react";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-full p-4">
                <Scale className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Community guidelines for the Infrastructure Agents discovery
              platform
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: January 31, 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-12 legal-page-intro-box rounded-xl p-8">
            <div className="flex items-start mb-4">
              <Bot className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold legal-page-section-title mb-3">
                  Welcome to InfrAgents
                </h2>
                <p className="legal-page-text leading-relaxed mb-4">
                  InfrAgents.com is a community-driven discovery platform for
                  Infrastructure Agents—autonomous software entities that use
                  AI, machine learning, and advanced reasoning to manage and
                  optimize cloud and on-premises environments by aligning
                  infrastructure operations with organizational goals.
                </p>
                <p className="legal-page-text leading-relaxed">
                  The purpose of this platform is to enable the discovery and
                  sharing of open-source GitHub projects related to
                  Infrastructure Agents. We do not host, operate, or provide
                  Infrastructure Agents ourselves. All listed agents remain
                  under the full control and responsibility of their respective
                  creators and maintainers.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="space-y-8">
            {/* Acceptance of Terms */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <FileText className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  1. Acceptance and Scope
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  By accessing InfrAgents.com, you agree to these Terms of
                  Service and acknowledge that our role is limited to providing
                  a catalog and discovery platform. We do not develop, host,
                  operate, or provide technical support for any Infrastructure
                  Agents listed on the site.
                </p>
              </div>
            </section>

            {/* Platform Use */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Search className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  2. Platform Usage
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  The platform is intended to be used for discovering and
                  researching Infrastructure Agents, submitting open-source
                  projects for community discovery, sharing knowledge and
                  experiences, and contributing to the community through reviews
                  and feedback.
                </p>

                <p>
                  Certain activities are strictly prohibited. These include
                  submitting projects that are not genuine Infrastructure
                  Agents, uploading malicious or misleading content, attempting
                  to compromise the security of the platform or its users, using
                  automated tools to scrape or overwhelm the platform, or
                  impersonating other users or organizations.
                </p>
              </div>
            </section>

            {/* Agent Submissions */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Github className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  3. Infrastructure Agent Submissions
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  All submitted projects must be genuine Infrastructure Agents
                  with autonomous or semi-autonomous capabilities that use
                  artificial intelligence, machine learning, or advanced
                  reasoning for infrastructure management. Projects must be
                  open-source, publicly accessible on GitHub, and include
                  appropriate documentation and licensing information.
                  Submissions must not contain malicious, harmful, or illegal
                  code.
                </p>

                <p>
                  By submitting a project, you confirm that you have the right
                  to share it publicly, that it complies with applicable laws
                  and regulations, and that you will keep its information
                  accurate. You also acknowledge that we reserve the right to
                  remove projects that do not meet these requirements or that
                  violate these Terms of Service.
                </p>
              </div>
            </section>

            {/* Platform Disclaimer */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  4. Platform Disclaimers
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <div className="legal-page-notice-box p-4 rounded-lg">
                  <h3 className="text-lg font-semibold legal-page-notice-title mb-2">
                    Important Notice
                  </h3>
                  <p className="legal-page-notice-text">
                    InfrAgents.com is a discovery platform only. We do not
                    develop, test, validate, or provide any Infrastructure
                    Agents. All agents listed are developed and maintained by
                    independent third parties.
                  </p>
                </div>

                <p>
                  The platform is provided "as is," without warranties of any
                  kind. We make no guarantees about the functionality, security,
                  or reliability of any listed Infrastructure Agents, the
                  accuracy or completeness of their descriptions, or their
                  suitability for specific use cases. We also cannot guarantee
                  uninterrupted or error-free operation of the platform itself.
                </p>

                <p>
                  Users are responsible for exercising due diligence when
                  evaluating and deploying Infrastructure Agents. Code should
                  always be reviewed, tested in non-production environments, and
                  assessed for compliance with organizational security policies
                  before deployment.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Shield className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  5. Limitation of Liability
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  To the maximum extent permitted by law, InfrAgents.com and its
                  operators shall not be liable for any direct, indirect,
                  incidental, special, or consequential damages that arise from
                  the use or inability to use the platform. This includes
                  damages related to the deployment or operation of
                  Infrastructure Agents discovered through the platform, data
                  loss, security breaches, system failures, business
                  interruptions, financial losses, or the actions of third
                  parties.
                </p>
                <p className="text-sm font-medium legal-page-text mt-4">
                  This limitation applies even if we have been informed of the
                  possibility of such damages.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <FileText className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  6. Intellectual Property
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  The design, code, and original content of the InfrAgents.com
                  platform are protected by intellectual property laws.
                  Infrastructure Agents listed on the platform remain the
                  property of their creators and are subject to their respective
                  open-source licenses.
                </p>
                <p>
                  By submitting content to the platform, you grant us a
                  non-exclusive, worldwide license to display and distribute
                  your submission for the purpose of operating the discovery
                  platform.
                </p>
              </div>
            </section>

            {/* Community Standards */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Users className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  7. Community Standards
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  We encourage an open, collaborative community based on
                  transparency, respect, and shared learning. All members are
                  expected to provide accurate information, respect intellectual
                  property rights, engage professionally with others, report
                  security issues responsibly, and help preserve the quality and
                  integrity of the platform.
                </p>
              </div>
            </section>

            {/* Modifications and Termination */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Scale className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  8. Modifications and Termination
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  We may update these Terms of Service from time to time, with
                  changes taking effect once they are posted. If significant
                  changes are made, we will communicate them through
                  announcements on the platform.
                </p>
                <p>
                  We reserve the right to suspend or terminate access to the
                  platform in cases of violations of these terms. While we aim
                  to provide reasonable notice before taking such action, we may
                  act immediately in cases of severe misconduct or security
                  threats.
                </p>
              </div>
            </section>
          </div>

          {/* Contact Section */}
          <section className="mt-12">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-8 text-white">
              <div className="text-center">
                <Mail className="h-12 w-12 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-4">
                  Questions About These Terms?
                </h2>
                <p className="text-lg mb-6 text-orange-100 max-w-2xl mx-auto">
                  We believe in transparency and open communication. If you have
                  questions about these terms or need clarification on platform
                  policies, we're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="mailto:agent@infragents.com"
                    className="inline-flex items-center justify-center bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    agent@infragents.com
                  </a>
                  <a
                    href="https://github.com/chrisurf/infragents/discussions/2"
                    className="inline-flex items-center justify-center bg-white/10 text-white border border-white/20 px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
                  >
                    <Github className="h-4 w-4 mr-2" />
                    Community Discussions
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
